<script lang="ts">
  import { onMount } from 'svelte';
  import { UserService, type UserProfileUpdateData, type UserUpdateResponse } from '../../lib/api/user.service';
  import { userStore } from '../../stores/auth.store';
  import { AuthService, type User } from '../../lib/api/auth.service';
  import Button from '../../components/Button.svelte';
  import Input from '../../components/Input.svelte';
  import toast from 'svelte-french-toast';

  let loading = false;
  let initializing = true;
  let fieldErrors: { [key: string]: string } = {};
  let user: User | null = null;

  // Removed tabs - consolidated into single screen

  // Email change modal state
  let showEmailModal = false;
  let emailLoading = false;
  let emailData = {
    email: ''
  };

  let profileData: UserProfileUpdateData = {
    full_name: '',
    phone: ''
  };

  // Load user data on mount
  onMount(async () => {
    await loadUserData();
  });

  async function loadUserData() {
    try {
      initializing = true;
      const localUser = AuthService.getUser();
      
      if (localUser) {
        user = localUser;
        initializeProfileData();
      } else {
        const result = await AuthService.getCurrentUser();
        if (result.success && result.user) {
          user = result.user;
          initializeProfileData();
        } else {
          toast.error('Failed to load user data');
        }
      }
    } catch (error: any) {
      console.error('Error loading user:', error);
      toast.error('Failed to load user data');
    } finally {
      initializing = false;
    }
  }

  function initializeProfileData() {
    if (!user) return;
    
    try {
      profileData = {
        full_name: user.full_name || '',
        phone: user.phone || ''
      };
      fieldErrors = {};
    } catch (error: any) {
      console.error('Failed to initialize profile data:', error);
    }
  }

  function validateForm(): boolean {
    fieldErrors = {};
    let isValid = true;

    // Validate required fields
    if (!profileData.full_name || profileData.full_name.trim() === '') {
      fieldErrors.full_name = 'Full name is required';
      isValid = false;
    }

    if (!profileData.phone || profileData.phone.trim() === '') {
      fieldErrors.phone = 'Phone number is required';
      isValid = false;
    }

    return isValid;
  }

  function getFieldError(fieldName: string): string | undefined {
    return fieldErrors[fieldName] || undefined;
  }

  async function handleSubmit() {
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    loading = true;
    fieldErrors = {};
    
    try {
      const result: UserUpdateResponse = await UserService.updateUserProfile(profileData);
      
      if (result.success && result.user) {
        // Update user store with the updated user data
        userStore.set(result.user);
        user = result.user;
        
        toast.success(result.message || 'Profile updated successfully!');
      } else {
        // Handle UserService error response
        if (result.error?.response?.data?.data?.errors) {
          const errors = result.error.response.data.data.errors;
          Object.keys(errors).forEach(field => {
            fieldErrors[field] = Array.isArray(errors[field]) ? errors[field][0] : errors[field];
          });
        }
        toast.error(result.message || 'Failed to update profile. Please try again.');
      }
    } catch (error: any) {
      console.error('Profile update error:', error);
      
      if (error?.response?.data?.data?.errors) {
        const errors = error.response.data.data.errors;
        Object.keys(errors).forEach(field => {
          fieldErrors[field] = Array.isArray(errors[field]) ? errors[field][0] : errors[field];
        });
      } else if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to update profile. Please try again.');
      }
    } finally {
      loading = false;
    }
  }

  function openEmailModal() {
    emailData = {
      email: user?.email || ''
    };
    showEmailModal = true;
  }

  function closeEmailModal() {
    showEmailModal = false;
    emailData = { email: '' };
  }

  async function handleEmailUpdate() {
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailData.email.trim()) {
      toast.error('Email is required');
      return;
    }
    
    if (!emailRegex.test(emailData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    emailLoading = true;
    try {
      const result = await AuthService.updateEmail({
        email: emailData.email
      });
      
      if (result.success) {
        toast.success('Email sent successfully! Please check your email address for verification link.');
        closeEmailModal();
      } else {
        toast.error((result as any)?.error?.response?.data?.data?.errors?.email || 'Failed to send email. Please try again.');
      }
    } catch (error: any) {
      // Handle specific error messages
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error?.response?.data?.errors) {
        const errors = error.response.data.errors;
        const errorMessages = Object.values(errors).flat().join(', ');
        toast.error(errorMessages);
      } else {
        toast.error('Failed to send email. Please try again.');
      }
    } finally {
      emailLoading = false;
    }
  }

  function handleEmailModalKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      closeEmailModal();
    } else if (event.key === 'Enter' && !emailLoading) {
      handleEmailUpdate();
    }
  }
</script>

<div class="profile-page">
  {#if initializing}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading profile...</p>
    </div>
  {:else}
    <div class="page-header">
      <h1>Edit Employee Profile</h1>
      <p>Manage your account settings and personal information</p>
    </div>
    
    <!-- Consolidated Profile and Email -->
    <div class="profile-container">
      <form on:submit|preventDefault={handleSubmit}>
        <!-- Personal Information Section -->
        <div class="section">
          <h2>Personal Information</h2>
          
          <div class="form-row">
            <div class="form-group">
              <Input 
                label="Full Name"
                placeholder="Enter your full name"
                bind:value={profileData.full_name}
                required={true}
                error={getFieldError('full_name')}
                disabled={loading}
              />
            </div>
            
            <div class="form-group">
              <Input 
                label="Phone Number"
                type="tel"
                placeholder="Enter your phone number"
                bind:value={profileData.phone}
                required={true}
                error={getFieldError('phone')}
                disabled={loading}
              />
            </div>
          </div>
        </div>

        <!-- Email Address Section -->
        <div class="section">
          <h2>Email Address</h2>
          <p class="section-description">Update your account email address. A verification email will be sent to confirm the change.</p>
          
          <div class="email-section">
            <div class="current-email-card">
              <div class="current-email-header">
                <svg class="email-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                <h3>Current Primary Email</h3>
              </div>
              <div class="current-email-value">
                {user?.email || 'Not provided'}
              </div>
            </div>
            
            <div class="email-actions">
              <Button 
                variant="primary" 
                on:click={openEmailModal}
                disabled={loading || emailLoading}
                type="button"
              >
                Change Email
              </Button>
            </div>
          </div>
        </div>
        
        <div class="info-section">
          <div class="info-card">
            <h4>👤 Personal Information</h4>
            <p>Update your personal details and email address. Required fields are marked with an asterisk (*).</p>
          </div>
        </div>
        
        <div class="form-actions">
          <Button 
            type="submit" 
            variant="primary" 
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update Profile'}
          </Button>
        </div>
      </form>
    </div>
  {/if}
</div>

<!-- Email Change Modal -->
{#if showEmailModal}
  <div 
    class="modal-overlay" 
    role="presentation"
    on:click|self={closeEmailModal}
    on:keydown={(e) => e.key === 'Escape' && closeEmailModal()}
  >
    <div 
      class="modal-content" 
      role="dialog"
      aria-modal="true"
      aria-labelledby="email-modal-title"
    >
      <div class="modal-header">
        <h3 id="email-modal-title">Change Email Address</h3>
        <button class="modal-close" on:click={closeEmailModal}>×</button>
      </div>
      
      <div class="modal-body">
        <div class="form-group">
          <Input 
            type="email"
            label="New Email Address"
            bind:value={emailData.email}
            placeholder="Enter your new email address"
            required={true}
            fullWidth={true}
            disabled={emailLoading}
          />
        </div>
        
        <p class="info-text">
          📧 An email will be sent to the specified email address with a verification link to update your email.
        </p>
      </div>
      
      <div class="modal-footer">
        <Button 
          variant="secondary" 
          on:click={closeEmailModal}
          disabled={emailLoading}
        >
          Cancel
        </Button>
        <Button 
          variant="primary" 
          on:click={handleEmailUpdate}
          disabled={emailLoading || !emailData.email.trim()}
        >
          {emailLoading ? 'Sending...' : 'Send Verification Email'}
        </Button>
      </div>
    </div>
  </div>
{/if}

<style>
  .profile-page {
    min-height: 100vh;
    background-color: #f7fafc;
    padding: 1.5rem 2rem;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    gap: 1rem;
  }

  .loading-spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #e5e7eb;
    border-top-color: var(--color-primary);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .loading-container p {
    color: #6b7280;
    font-size: 0.875rem;
  }

  .page-header {
    margin-bottom: 2rem;
  }

  .page-header h1 {
    font-size: 1.875rem;
    font-weight: 700;
    color: #111827;
    margin: 0 0 0.5rem 0;
  }

  .page-header p {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
  }

  /* Email Section */
  .email-section {
    margin-top: 1.5rem;
  }

  .current-email-card {
    background-color: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .current-email-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }

  .current-email-header h3 {
    font-size: 1rem;
    font-weight: 600;
    color: #374151;
    margin: 0;
  }

  .email-icon {
    color: var(--color-primary);
    flex-shrink: 0;
  }

  .current-email-value {
    font-size: 1.125rem;
    font-weight: 500;
    color: #111827;
    word-break: break-all;
    padding: 0.75rem 1rem;
    background-color: white;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
  }

  .email-actions {
    display: flex;
    justify-content: flex-start;
    gap: 1rem;
  }

  .section-description {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0 0 1rem 0;
    line-height: 1.5;
  }

  .profile-container {
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
    padding: 2rem;
    max-width: 800px;
  }

  .section {
    margin-bottom: 2rem;
    padding-bottom: 2rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .section:last-of-type {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }

  .section h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 1.5rem 0;
  }

  .form-row {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .form-row:last-child {
    margin-bottom: 0;
  }

  .form-group {
    display: flex;
    flex-direction: column;
  }


  /* Modal Styles */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
  }

  .modal-content {
    background-color: #fff;
    border-radius: 12px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    width: 90%;
    max-width: 500px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: modalFadeIn 0.3s ease-out;
  }

  @keyframes modalFadeIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem;
    background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-hover) 100%);
    color: white;
  }

  .modal-header h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
  }

  .modal-close {
    background: none;
    border: none;
    color: white;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background-color 0.2s ease;
  }

  .modal-close:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }

  .modal-body {
    padding: 2rem;
  }

  .modal-body .form-group {
    margin-bottom: 1rem;
  }

  .info-text {
    margin-top: 1rem;
    font-size: 0.875rem;
    color: #6b7280;
    line-height: 1.5;
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    padding: 1.5rem;
    background-color: #f8fafc;
    border-top: 1px solid #e2e8f0;
  }

  .info-section {
    margin-top: 2rem;
    padding-top: 2rem;
    border-top: 1px solid #e5e7eb;
  }

  .info-card {
    background: #fffbeb;
    border: 1px solid #fed7aa;
    border-radius: 8px;
    padding: 1.25rem;
  }

  .info-card h4 {
    color: #d97706;
    font-size: 0.875rem;
    font-weight: 600;
    margin: 0 0 0.5rem 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .info-card p {
    color: #92400e;
    font-size: 0.875rem;
    line-height: 1.5;
    margin: 0;
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 2rem;
    padding-top: 2rem;
    border-top: 1px solid #e5e7eb;
  }

  @media (max-width: 768px) {
    .profile-page {
      padding: 1rem;
    }

    .current-email-card {
      padding: 1.25rem;
    }

    .current-email-value {
      font-size: 1rem;
    }

    .profile-container {
      padding: 1.5rem;
    }

    .form-row {
      grid-template-columns: 1fr;
      gap: 1rem;
    }

    .page-header h1 {
      font-size: 1.5rem;
    }
  }
</style>

